import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import barStockConsumptionReportService from '../services/barStockConsumptionReportService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import {
  Calendar,
  ChevronRight,
  FileSpreadsheet,
  Loader2,
  Play,
  RefreshCw,
  Search,
} from 'lucide-react';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 1);
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

const BarStockConsumptionReportRunsPage = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [filters, setFilters] = useState({
    client_id: '',
    supplier_id: '',
    product_id: '',
    include_non_purchase_sources: false,
  });
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 1,
  });
  const pageSize = 20;

  const loadRuns = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await barStockConsumptionReportService.getRuns(page, pageSize);
      setRuns(data.runs || []);
      setPagination(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '加载报表记录失败');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name) => (checked) => {
    setFilters((prev) => ({ ...prev, [name]: checked }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setError('');
      const run = await barStockConsumptionReportService.createRun(
        dateRange.startDate,
        dateRange.endDate,
        filters
      );
      navigate(`/reports/bar-stock-consumption/runs/${run._id || run.run_id}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '生成报表失败');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewRun = (runId) => {
    navigate(`/reports/bar-stock-consumption/runs/${runId}`);
  };

  return (
    <Layout title="棒料采购消耗报表" activeTab="reports">
      <div className="max-w-[1600px] mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">棒料采购消耗报表</h1>
            <p className="text-sm text-muted-foreground mt-1">
              生成并管理棒料采购消耗报表运行记录
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadRuns} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-2">刷新</span>
            </Button>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Search className="h-4 w-4" />
              生成新报表
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">开始日期</Label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="startDate"
                    name="startDate"
                    type="date"
                    value={dateRange.startDate}
                    onChange={handleDateChange}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">结束日期</Label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="endDate"
                    name="endDate"
                    type="date"
                    value={dateRange.endDate}
                    onChange={handleDateChange}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_id">客户</Label>
                <Input
                  id="client_id"
                  name="client_id"
                  placeholder="客户 ID"
                  value={filters.client_id}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplier_id">供应商</Label>
                <Input
                  id="supplier_id"
                  name="supplier_id"
                  placeholder="供应商 ID"
                  value={filters.supplier_id}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="product_id">品名/物料编码</Label>
                <Input
                  id="product_id"
                  name="product_id"
                  placeholder="物料编码"
                  value={filters.product_id}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="space-y-2 flex flex-col justify-center">
                <Label htmlFor="include_non_purchase_sources" className="cursor-pointer">
                  包含非采购来源
                </Label>
                <div className="flex items-center space-x-2 h-10">
                  <Switch
                    id="include_non_purchase_sources"
                    checked={filters.include_non_purchase_sources}
                    onCheckedChange={handleSwitchChange('include_non_purchase_sources')}
                  />
                  <span className="text-sm text-muted-foreground">余料/手动/外协等</span>
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={handleGenerate} disabled={generating}>
                {generating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                <span className="ml-2">生成报表</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="mb-4 p-4 rounded-md bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              历史报表记录
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="whitespace-nowrap">运行编号</TableHead>
                    <TableHead className="whitespace-nowrap">报表名称</TableHead>
                    <TableHead className="whitespace-nowrap">日期范围</TableHead>
                    <TableHead className="whitespace-nowrap">非采购来源</TableHead>
                    <TableHead className="text-right whitespace-nowrap">行数</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期购入</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期领用</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期库存</TableHead>
                    <TableHead className="whitespace-nowrap">生成人</TableHead>
                    <TableHead className="whitespace-nowrap">生成时间</TableHead>
                    <TableHead className="text-right whitespace-nowrap">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && runs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mt-2">加载中...</p>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && runs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                        暂无报表记录，点击上方「生成报表」创建第一份报表
                      </TableCell>
                    </TableRow>
                  )}
                  {runs.map((run) => (
                    <TableRow
                      key={run._id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleViewRun(run._id || run.run_id)}
                    >
                      <TableCell className="whitespace-nowrap font-medium">{run.run_id}</TableCell>
                      <TableCell className="whitespace-nowrap">{run.report_name}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {run.filters?.start_date} ~ {run.filters?.end_date}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {run.filters?.include_non_purchase_sources ? '是' : '否'}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">{run.row_count}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {formatNumber(run.summary?.total_purchase_quantity)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {formatNumber(run.summary?.total_consumption_quantity)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {formatNumber(run.summary?.total_inventory_quantity)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {run.created_by_name || '-'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDateTime(run.created_at)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewRun(run._id || run.run_id);
                          }}
                        >
                          查看 <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  第 {pagination.page} / {pagination.total_pages} 页，共 {pagination.total} 条
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                  >
                    上一页
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                    disabled={page >= pagination.total_pages || loading}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default BarStockConsumptionReportRunsPage;
